import React from "react";
import { ActionMeta, OptionsType } from "react-select";
import AsyncSelect from "react-select/async";
import HeaderNavBar from "../components/HeaderNavBar";
import SwapButton from "../components/SwapButton";
import SwapButtonVertical from "../components/SwapButtonVertical";
import DepartureTimePicker from "../components/DepartureTimePicker";
import * as StationAPI from "../api/station-api";
import * as APIUtils from "../api/api-utils";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { LocationDescriptor } from "history";
import AppNavigationState from "../utils/AppNavigationState";
import { StationDefinition } from "../api/api-data-model";
import { StaticContext } from "react-router";
import { withTranslation } from "react-i18next";
import { WithTranslationLangProps } from "../utils/i18next/with-translation-keys";
import { Trans } from "react-i18next";
import picto1 from "../resources/images/Pictogramme Fiabilité des transports.svg";
import picto2 from "../resources/images/Pictogramme Probabilité.svg";
import picto3 from "../resources/images/Pictogramme Attention.svg";

interface ISearchJourneyPageProps
  extends WithTranslationLangProps,
    RouteComponentProps<{}, StaticContext, AppNavigationState | undefined> {}

interface ISearchJourneyPageState {
  areStationsLoading: boolean;
  stationFrom: IStationDef | undefined;
  stationTo: IStationDef | undefined;
  selectedDate: Date;
  stations: OptionsType<IStationDef>;
  redirect?: LocationDescriptor<AppNavigationState>;
}

interface IStationDef {
  value: string;
  label: string;
}

class SearchJourneyPage extends React.Component<
  ISearchJourneyPageProps,
  ISearchJourneyPageState
> {
  constructor(props: ISearchJourneyPageProps) {
    super(props);
    this.state = {
      areStationsLoading: false,
      selectedDate:
        this.props.location.state?.searchContext?.searchDate ?? new Date(),
      stationFrom: this.toIStationDef(
        this.props.location.state?.searchContext?.fromStation
      ),
      stationTo: this.toIStationDef(
        this.props.location.state?.searchContext?.toStation
      ),
      stations: [],
    };
    this.loadStations = this.loadStations.bind(this);
    this.getNoOptionMessage = this.getNoOptionMessage.bind(this);
    this.handleDateChanged = this.handleDateChanged.bind(this);
    this.handleFromChanged = this.handleFromChanged.bind(this);
    this.handleToChanged = this.handleToChanged.bind(this);
    this.handleSwapButtonClicked = this.handleSwapButtonClicked.bind(this);
    this.handleSearchClicked = this.handleSearchClicked.bind(this);
  }

  private toIStationDef(
    stationdef?: StationDefinition
  ): IStationDef | undefined {
    if (stationdef) {
      return { value: stationdef.id, label: stationdef.name };
    }
    return undefined;
  }

  private async loadStations(inputValue: string): Promise<IStationDef[]> {
    try {
      let stationsResult = await StationAPI.getStations(inputValue, 20);
      let stations = stationsResult.map((s) => {
        return { value: s.id, label: s.name };
      });
      return stations;
    } catch (error) {
      return [];
    }
  }

  private getNoOptionMessage(params: { inputValue: string }): string {
    if (params.inputValue === undefined || params.inputValue === "") return "-";
    else return this.props.t("no_station_found");
  }

  private handleDateChanged(date: Date) {
    this.setState({
      selectedDate: date,
    });
  }

  private handleFromChanged(value: any, _actionMeta: ActionMeta<Object>) {
    this.setState({
      stationFrom: value as IStationDef,
    });
  }

  private handleToChanged(value: any, _actionMeta: ActionMeta<Object>) {
    this.setState({
      stationTo: value as IStationDef,
    });
  }

  private handleSwapButtonClicked() {
    this.setState({
      stationFrom: this.state.stationTo,
      stationTo: this.state.stationFrom,
    });
  }

  private handleSearchClicked() {
    let from = this.state.stationFrom?.value;
    let fromStation: StationDefinition | undefined = undefined;
    if (this.state.stationFrom) {
      fromStation = {
        id: this.state.stationFrom.value,
        name: this.state.stationFrom.label,
      };
    }
    let to = this.state.stationTo?.value;
    let toStation: StationDefinition | undefined = undefined;
    if (this.state.stationTo) {
      toStation = {
        id: this.state.stationTo.value,
        name: this.state.stationTo.label,
      };
    }
    let date = APIUtils.dateToAPIString(this.state.selectedDate);
    if (from && to) {
      let redirect = {
        pathname: `/journey/search/${from}/${to}/${date}`,
        state: {
          searchContext: {
            fromStation: fromStation,
            toStation: toStation,
            searchDate: this.state.selectedDate,
          },
        },
      };
      this.setState({ redirect: redirect });
    }
  }

  render() {
    if (this.state.redirect) return <Redirect push to={this.state.redirect} />;
    return (
      <>
        <div className="with-back-header">
          <HeaderNavBar transparency />
        </div>
        <div id="search-tile">
          <div className="bg-title back-opacity-09 tile-title">
            {this.props.t("plan_a_trip")}
          </div>
          <div className="tile back-opacity-09">
            <div className="d-flex flex-wrap">
              <div className="flex-basis-100 flex-basis-md-45">
                <h4>{this.props.t("from_station")}</h4>
                <AsyncSelect
                  value={this.state.stationFrom}
                  onChange={this.handleFromChanged}
                  loadOptions={this.loadStations}
                  isClearable={true}
                  noOptionsMessage={this.getNoOptionMessage}
                  loadingMessage={() => this.props.t("loading_withPeriod")}
                  placeholder={this.props.t("find_a_station")}
                />
              </div>
              <SwapButton
                className="flex-basis-100 align-self-end flex-basis-md-10 d-none d-md-block"
                height="2.375rem"
                onClick={this.handleSwapButtonClicked}
              />
              <SwapButtonVertical
                className="flex-basis-100 align-self-end flex-basis-md-10 d-md-none pt-1"
                height="2.375rem"
                onClick={this.handleSwapButtonClicked}
              />
              <div className="flex-basis-100 flex-basis-md-45">
                <h4>{this.props.t("to_station")}</h4>
                <AsyncSelect
                  value={this.state.stationTo}
                  onChange={this.handleToChanged}
                  loadOptions={this.loadStations}
                  isClearable={true}
                  noOptionsMessage={this.getNoOptionMessage}
                  loadingMessage={() => this.props.t("loading_withPeriod")}
                  placeholder={this.props.t("find_a_station")}
                />
              </div>
            </div>
            <div className="d-flex flex-wrap pt-4">
              <div className="flex-basis-100 flex-basis-md-45">
                <DepartureTimePicker
                  date={this.state.selectedDate}
                  onDateChanged={this.handleDateChanged}
                />
              </div>
              <div className="flex-basis-100 flex-basis-md-10"></div>
              <div className="pt-4 pt-md-0 flex-basis-100 flex-basis-md-45">
                <button
                  disabled={!(this.state.stationFrom && this.state.stationTo)}
                  className="prveo-btn prveo-btn-primary prveo-btn-large d-block ml-md-auto mr-md-0 m-auto"
                  onClick={this.handleSearchClicked}
                >
                  {this.props.t("search")}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div id="previseo-description" className="tile back-opacity-09">
          <div id="previseo-whatis" className="d-flex">
            <img
              src={picto1}
              className="d-inline-block align-top navbar-brand-picto"
              style={{ marginRight: "10px"}}
              alt="Qu'est-ce que Previseo ?"
            />
            <div>
              <Trans>{this.props.t("previseo_whatis")}</Trans>
            </div>
          </div>
          <div id="previseo-probs" className="d-flex">
            <img
              src={picto2}
              className="d-inline-block align-top navbar-brand-picto"
              style={{ marginRight: "10px"}}
              alt="Intervalle des probabilités"
            />
            <div>
              <Trans>{this.props.t("previseo_probs")}</Trans>
            </div>
          </div>
          <div id="previseo-limits" className="d-flex">
            <img
              src={picto3}
              className="d-inline-block align-top navbar-brand-picto"
              style={{ marginRight: "10px"}}
              alt="Limites de Previseo"
            />
            <div>
              <Trans>{this.props.t("previseo_limits")}</Trans>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default withTranslation()(SearchJourneyPage);
